/**
 * Утилиты для оптимизации аудио на мобильных устройствах
 */

export interface MobileAudioConfig {
  src: string;
  volume?: number;
  preload?: 'none' | 'metadata' | 'auto';
  playsinline?: boolean;
}

export class MobileAudioManager {
  private audioPools: Map<string, HTMLAudioElement[]> = new Map();
  private audioBuffers: Map<string, ArrayBuffer> = new Map();
  private userInteracted = false;
  private audioReady = false;
  private lastPlayTime = 0;
  private readonly AUDIO_COOLDOWN = 20; // Минимальный интервал между звуками в мс
  private readonly POOL_SIZE = 3; // Количество аудио элементов в пуле
  private isSafari = false; // Флаг для определения Safari

  constructor() {
    // Определяем Safari (убрали специальные оптимизации)
    this.isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent) || 
                   /iPad|iPhone|iPod/.test(navigator.userAgent);
  }

  /**
   * Инициализирует пул аудио элементов для надежного воспроизведения
   */
  async preloadAudio(key: string, config: MobileAudioConfig): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        const audioPool: HTMLAudioElement[] = [];
        
        // Создаем пул аудио элементов
        for (let i = 0; i < this.POOL_SIZE; i++) {
          const audio = new Audio();
          audio.src = config.src;
          audio.volume = config.volume ?? 0.5;
          audio.preload = config.preload ?? 'auto';
          
          // Критически важные атрибуты для мобильных устройств
          audio.setAttribute('playsinline', 'true');
          audio.setAttribute('webkit-playsinline', 'true');
          audio.setAttribute('x-webkit-airplay', 'allow');
          audio.setAttribute('preload', 'auto');
          
          // Отключаем управление громкостью системой
          audio.controls = false;
          
          // Специальные настройки для Safari
          if (this.isSafari) {
            audio.setAttribute('webkit-playsinline', 'true');
            audio.setAttribute('playsinline', 'true');
            // Safari лучше работает с более низкой громкостью
            audio.volume = Math.min(config.volume ?? 0.5, 0.4);
          }
          
          audioPool.push(audio);
        }
        
        this.audioPools.set(key, audioPool);
        
        // Загружаем первый элемент для проверки готовности
        const firstAudio = audioPool[0];
        firstAudio.load();

        const handleCanPlayThrough = () => {
          firstAudio.removeEventListener('canplaythrough', handleCanPlayThrough);
          firstAudio.removeEventListener('error', handleError);
          
          // Загружаем остальные элементы в фоне
          audioPool.slice(1).forEach(audio => {
            audio.load();
          });
          
          resolve();
        };

        const handleError = (error: Event) => {
          firstAudio.removeEventListener('canplaythrough', handleCanPlayThrough);
          firstAudio.removeEventListener('error', handleError);
          console.warn(`Ошибка загрузки аудио ${key}:`, error);
          reject(error);
        };

        firstAudio.addEventListener('canplaythrough', handleCanPlayThrough, { once: true });
        firstAudio.addEventListener('error', handleError, { once: true });

        // Короткий таймаут для загрузки
        const timeout = 2000; // 2 секунды для всех устройств
        setTimeout(() => {
          if (!this.audioPools.has(key)) {
            firstAudio.removeEventListener('canplaythrough', handleCanPlayThrough);
            firstAudio.removeEventListener('error', handleError);
            console.warn(`Таймаут загрузки аудио ${key}`);
            reject(new Error('Audio load timeout'));
          }
        }, timeout);

      } catch (error) {
        console.warn(`Ошибка при создании аудио пула ${key}:`, error);
        reject(error);
      }
    });
  }

  /**
   * Воспроизводит звук с использованием пула аудио элементов
   */
  async playAudio(key: string, options?: { volume?: number; immediate?: boolean }): Promise<void> {
    if (!this.userInteracted) {
      console.log('Пользователь не взаимодействовал, пропускаем звук');
      return;
    }

    const audioPool = this.audioPools.get(key);
    if (!audioPool || audioPool.length === 0) {
      console.warn(`Аудио пул ${key} не найден`);
      return;
    }

    // Проверяем минимальный интервал между звуками
    const now = Date.now();
    if (now - this.lastPlayTime < this.AUDIO_COOLDOWN) {
      return;
    }
    this.lastPlayTime = now;

    // Находим свободный аудио элемент
    let availableAudio: HTMLAudioElement | null = null;
    
    for (const audio of audioPool) {
      if (audio.paused || audio.ended) {
        availableAudio = audio;
        break;
      }
    }

    // Если нет свободных, используем первый и сбрасываем его
    if (!availableAudio) {
      availableAudio = audioPool[0];
      availableAudio.pause();
      availableAudio.currentTime = 0;
    }

    try {
      // Сбрасываем время воспроизведения
      availableAudio.currentTime = 0;
      
      // Устанавливаем громкость
      if (options?.volume !== undefined) {
        availableAudio.volume = options.volume;
      }

      // Для немедленного воспроизведения
      if (options?.immediate) {
        // Устанавливаем минимальную громкость для быстрого воспроизведения
        const originalVolume = availableAudio.volume;
        const startVolume = 0.15;
        availableAudio.volume = startVolume;
        
        const playPromise = availableAudio.play();
        if (playPromise !== undefined) {
          playPromise
            .then(() => {
              // Восстанавливаем громкость
              setTimeout(() => {
                availableAudio!.volume = originalVolume;
              }, 50);
            })
            .catch((error) => {
              console.warn(`Ошибка воспроизведения аудио ${key}:`, error);
              availableAudio!.volume = originalVolume;
            });
        }
      } else {
        // Обычное воспроизведение
        await availableAudio.play();
      }
    } catch (error) {
      console.warn(`Ошибка при воспроизведении аудио ${key}:`, error);
    }
  }

  /**
   * Останавливает все воспроизведения для данного звука
   */
  stopAudio(key: string): void {
    const audioPool = this.audioPools.get(key);
    if (audioPool) {
      audioPool.forEach(audio => {
        audio.pause();
        audio.currentTime = 0;
      });
    }
  }

  /**
   * Плавно изменяет громкость всех элементов в пуле
   */
  fadeAudio(key: string, targetVolume: number, duration: number): void {
    const audioPool = this.audioPools.get(key);
    if (!audioPool) return;

    audioPool.forEach(audio => {
      const startVolume = audio.volume;
      const volumeChange = targetVolume - startVolume;
      const steps = 20;
      const stepDuration = duration / steps;
      const volumeStep = volumeChange / steps;

      let currentStep = 0;
      const fadeInterval = setInterval(() => {
        currentStep++;
        audio.volume = startVolume + (volumeStep * currentStep);
        
        if (currentStep >= steps) {
          audio.volume = targetVolume;
          clearInterval(fadeInterval);
        }
      }, stepDuration);
    });
  }

  /**
   * Отмечает взаимодействие пользователя
   */
  setUserInteracted(): void {
    this.userInteracted = true;
    console.log('Пользователь взаимодействовал, аудио разблокировано');
  }

  /**
   * Устанавливает готовность аудио
   */
  setAudioReady(): void {
    this.audioReady = true;
  }

  /**
   * Проверяет готовность аудио
   */
  isReady(): boolean {
    return this.userInteracted;
  }

  /**
   * Очищает ресурсы
   */
  cleanup(): void {
    this.audioPools.forEach(audioPool => {
      audioPool.forEach(audio => {
        audio.pause();
        audio.src = '';
      });
    });
    this.audioPools.clear();
    this.audioBuffers.clear();
  }

  /**
   * Принудительно воспроизводит звук (для отладки)
   */
  forcePlayAudio(key: string): void {
    const audioPool = this.audioPools.get(key);
    if (audioPool && audioPool.length > 0) {
      const audio = audioPool[0];
      audio.currentTime = 0;
      audio.volume = 0.3;
      audio.play().catch(error => {
        console.warn('Принудительное воспроизведение не удалось:', error);
      });
    }
  }
}

/**
 * Глобальный экземпляр MobileAudioManager
 */
export const mobileAudioManager = new MobileAudioManager();

/**
 * Настройка разблокировки аудио для мобильных устройств
 */
export function setupMobileAudioUnlock(): void {
  const events = ['touchstart', 'click', 'keydown', 'mousedown'];
  
  const handleUserInteraction = () => {
    mobileAudioManager.setUserInteracted();
    
    // Удаляем все обработчики после первого взаимодействия
    events.forEach(event => {
      document.removeEventListener(event, handleUserInteraction);
    });
  };

  events.forEach(event => {
    document.addEventListener(event, handleUserInteraction, { once: true, passive: true });
  });
}

/**
 * Проверяет, является ли устройство мобильным
 */
export function isMobileDevice(): boolean {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
}

/**
 * Оптимизирует аудио элемент для мобильных устройств
 */
export function optimizeAudioForMobile(audio: HTMLAudioElement): void {
  audio.setAttribute('playsinline', 'true');
  audio.setAttribute('webkit-playsinline', 'true');
  audio.setAttribute('x-webkit-airplay', 'allow');
  audio.setAttribute('preload', 'auto');
  audio.controls = false;
}

/**
 * Создает оптимизированный аудио элемент для мобильных устройств
 */
export function createMobileAudio(src: string, volume: number = 0.5): HTMLAudioElement {
  const audio = new Audio(src);
  audio.volume = volume;
  optimizeAudioForMobile(audio);
  return audio;
}

/**
 * Создает пул аудио элементов для надежного воспроизведения
 */
export function createAudioPool(src: string, size: number = 3, volume: number = 0.5): HTMLAudioElement[] {
  const pool: HTMLAudioElement[] = [];
  
  for (let i = 0; i < size; i++) {
    const audio = createMobileAudio(src, volume);
    pool.push(audio);
  }
  
  return pool;
}
